export interface SidebarLogoConfig {
  onDark: string;
  onLight: string;
}

export type BrandingConfigs = Record<
  string,
  {
    title: string;
    isCarteBlanche: boolean;
    /**
     * Короткое имя для short_name манифеста и og:site_name.
     * Не заполнено — берётся часть title до тире.
     */
    shortName?: string;
    /**
     * Описание для og:description — карточка превью ссылки в мессенджерах.
     * Не заполнено — тег не пишется, превью остаётся с одним заголовком.
     */
    description?: string;
    /**
     * Цвет для <meta name="theme-color"> и theme_color в манифесте.
     * Совпадает с --axenta-primary темы бренда (src/styles.less).
     * Не заполнено — берётся цвет дефолтного бренда.
     */
    themeColor?: string;
    folderWithIcons: string;
    dataThemeAttr: string;
    faviconType: 'png' | 'svg';
    logo: {
      favicon: { type: 'png' | 'svg'; icon: string };
      login: { type: 'png' | 'svg'; onDark: string; onLight: string };
      sidebar: {
        useExpandedCollapsed: boolean;
        type?: 'png' | 'svg';
        default?: SidebarLogoConfig;
        expanded?: SidebarLogoConfig;
        collapsed?: SidebarLogoConfig;
      };
    };
    customBackgroundImages?: {
      onDark: string;
      onLight: string;
    };
    className?: string;
    // AXENTA-2230, информация о версии и метрологически-значимой части ПО
    versionInfo?: string;
  }
>;

const pathToBrandingFolder = 'common/assets/branding';

const versionInfoResurscontrol =
  'Версия GUI: 0.3.2 от 12.03.2026\n' +
  'MD5 GUI: f6bb1f5457b3fe5e4636be7e5cb4735c\n' +
  'Версия метрологически-значимой части: 0.3.2-М от 23.03.2026\n' +
  'MD5 метрологически-значимой части: 3d6097142139f7ad2553b2d84e792f36';

export const BRANDING_CONFIGS: BrandingConfigs = {
  default: {
    title: 'Axenta - Система мониторинга мобильных объектов',
    isCarteBlanche: true,
    themeColor: '#10b981',
    folderWithIcons: `${pathToBrandingFolder}/axenta/logo`,
    dataThemeAttr: '',
    faviconType: 'png',
    logo: {
      favicon: {
        type: 'png',
        icon: `${pathToBrandingFolder}/axenta/logo/favicon.png`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/axenta/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/axenta/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/axenta/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/axenta/logo/sidebar.svg`,
        },
      },
    },
  },
  'monitor.navitrade.ru': {
    title: 'мониторинг.навитрейд.рф',
    isCarteBlanche: true,
    themeColor: '#e42424',
    folderWithIcons: `${pathToBrandingFolder}/navitrade/logo`,
    dataThemeAttr: 'navitrade',
    faviconType: 'svg',
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/navitrade/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/navitrade/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/navitrade/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/navitrade/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/navitrade/logo/sidebar.svg`,
        },
      },
    },
    customBackgroundImages: {
      onDark: `${pathToBrandingFolder}/navitrade/login/light.webp`,
      onLight: `${pathToBrandingFolder}/navitrade/login/light.webp`,
    },
  },
  'glonassagro.ru': {
    title: 'ЮКАС-Агро - системы точного земледелия',
    isCarteBlanche: false,
    themeColor: '#10b981',
    folderWithIcons: `${pathToBrandingFolder}/glonassagro`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/glonassagro/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/glonassagro/logo/login.svg`,
        onLight: `${pathToBrandingFolder}/glonassagro/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/glonassagro/logo/sidebar.svg`,
          onLight: `${pathToBrandingFolder}/glonassagro/logo/sidebar.svg`,
        },
      },
    },
    dataThemeAttr: '',
    faviconType: 'svg',
  },
  'gpsmonitoring.resurscontrol.by': {
    title: 'Resurscontrol - мониторинг транспорта и контроль топлива',
    isCarteBlanche: false,
    themeColor: '#064acf',
    folderWithIcons: `${pathToBrandingFolder}/resurscontrol`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/resurscontrol/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/resurscontrol/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/resurscontrol/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/resurscontrol/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/resurscontrol/logo/sidebar.svg`,
        },
      },
    },
    customBackgroundImages: {
      onDark: `${pathToBrandingFolder}/resurscontrol/login/night.webp`,
      onLight: `${pathToBrandingFolder}/resurscontrol/login/light.webp`,
    },
    dataThemeAttr: 'resurscontrol',
    faviconType: 'svg',
    versionInfo: versionInfoResurscontrol,
  },
  'gps.stavtrack.ru': {
    title: 'Stavtrack',
    isCarteBlanche: false,
    themeColor: '#ff551f',
    folderWithIcons: `${pathToBrandingFolder}/stavtrack`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/stavtrack/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/stavtrack/logo/login.svg`,
        onLight: `${pathToBrandingFolder}/stavtrack/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: true,
        expanded: {
          onDark: `${pathToBrandingFolder}/stavtrack/logo/sidebar-expanded.svg`,
          onLight: `${pathToBrandingFolder}/stavtrack/logo/sidebar-expanded.svg`,
        },
        collapsed: {
          onDark: `${pathToBrandingFolder}/stavtrack/logo/sidebar-collapsed.svg`,
          onLight: `${pathToBrandingFolder}/stavtrack/logo/sidebar-collapsed.svg`,
        },
      },
    },
    customBackgroundImages: {
      onDark: `${pathToBrandingFolder}/stavtrack/login/light.webp`,
      onLight: `${pathToBrandingFolder}/stavtrack/login/light.webp`,
    },
    dataThemeAttr: 'stavtrack',
    faviconType: 'svg',
  },
  'russiagps.ru': {
    title: 'Мониторинг TEXX',
    isCarteBlanche: false,
    themeColor: '#ed1a3b',
    folderWithIcons: `${pathToBrandingFolder}/russiagps`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/russiagps/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/russiagps/logo/login.svg`,
        onLight: `${pathToBrandingFolder}/russiagps/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: true,
        expanded: {
          onDark: `${pathToBrandingFolder}/russiagps/logo/sidebar-expanded.svg`,
          onLight: `${pathToBrandingFolder}/russiagps/logo/sidebar-expanded.svg`,
        },
        collapsed: {
          onDark: `${pathToBrandingFolder}/russiagps/logo/sidebar-collapsed.svg`,
          onLight: `${pathToBrandingFolder}/russiagps/logo/sidebar-collapsed.svg`,
        },
      },
    },
    dataThemeAttr: 'russiagps',
    faviconType: 'svg',
  },
  'hosting.agitron.ru': {
    title: 'ГеоСистем',
    isCarteBlanche: false,
    themeColor: '#0d8215',
    folderWithIcons: `${pathToBrandingFolder}/agitron`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/agitron/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/agitron/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/agitron/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/agitron/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/agitron/logo/sidebar.svg`,
        },
      },
    },
    dataThemeAttr: 'agitron',
    faviconType: 'svg',
  },
  'web.avtonavix.ru': {
    title: 'АВТОНАВИКС - ГЛОНАСС МОНИТОРИНГ ТРАНСПОРТА',
    isCarteBlanche: false,
    themeColor: '#009fe3',
    folderWithIcons: `${pathToBrandingFolder}/avtonavix`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/avtonavix/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/avtonavix/logo/login.svg`,
        onLight: `${pathToBrandingFolder}/avtonavix/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/avtonavix/logo/sidebar.svg`,
          onLight: `${pathToBrandingFolder}/avtonavix/logo/sidebar.svg`,
        },
      },
    },
    dataThemeAttr: 'avtonavix',
    faviconType: 'svg',
  },
  'a.glonass24.com': {
    title: 'ООО "ПК Системы контроля транспорта"',
    isCarteBlanche: false,
    shortName: 'ГЛОНАСС24',
    themeColor: '#00bfff',
    folderWithIcons: `${pathToBrandingFolder}/glonass24/logo`,
    dataThemeAttr: 'glonass24',
    faviconType: 'png',
    className: 'glonass24-branding',
    logo: {
      favicon: {
        type: 'png',
        icon: `${pathToBrandingFolder}/glonass24/logo/favicon.png`,
      },
      login: {
        type: 'png',
        onDark: `${pathToBrandingFolder}/glonass24/logo/sidebar.png`,
        onLight: `${pathToBrandingFolder}/glonass24/logo/sidebar.png`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        type: 'png',
        default: {
          onDark: `${pathToBrandingFolder}/glonass24/logo/sidebar.png`,
          onLight: `${pathToBrandingFolder}/glonass24/logo/sidebar.png`,
        },
      },
    },
  },
  'smt.fleetmatic.ru': {
    title: 'Fleetmatic',
    isCarteBlanche: false,
    themeColor: '#10b981',
    folderWithIcons: `${pathToBrandingFolder}/fleetmatic`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/fleetmatic/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/fleetmatic/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/fleetmatic/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/fleetmatic/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/fleetmatic/logo/sidebar.svg`,
        },
      },
    },
    dataThemeAttr: '',
    faviconType: 'svg',
  },
  'app.geosmt.ru': {
    title: 'Geoservice - система мониторинга',
    isCarteBlanche: false,
    themeColor: '#0c55bb',
    folderWithIcons: `${pathToBrandingFolder}/geosmt`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/geosmt/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/geosmt/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/geosmt/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: false,
        default: {
          onDark: `${pathToBrandingFolder}/geosmt/logo/sidebar-night.svg`,
          onLight: `${pathToBrandingFolder}/geosmt/logo/sidebar.svg`,
        },
      },
    },
    customBackgroundImages: {
      onDark: `${pathToBrandingFolder}/geosmt/login/night.jpg`,
      onLight: `${pathToBrandingFolder}/geosmt/login/light.jpg`,
    },
    dataThemeAttr: 'geosmt',
    faviconType: 'svg',
  },
  'w.avtoscan.com': {
    title: 'Автоскан',
    isCarteBlanche: false,
    themeColor: '#00367e',
    folderWithIcons: `${pathToBrandingFolder}/avtoscan`,
    logo: {
      favicon: {
        type: 'svg',
        icon: `${pathToBrandingFolder}/avtoscan/logo/favicon.svg`,
      },
      login: {
        type: 'svg',
        onDark: `${pathToBrandingFolder}/avtoscan/logo/login-night.svg`,
        onLight: `${pathToBrandingFolder}/avtoscan/logo/login.svg`,
      },
      sidebar: {
        useExpandedCollapsed: true,
        expanded: {
          onDark: `${pathToBrandingFolder}/avtoscan/logo/sidebar-expanded-night.svg`,
          onLight: `${pathToBrandingFolder}/avtoscan/logo/sidebar-expanded.svg`,
        },
        collapsed: {
          onDark: `${pathToBrandingFolder}/avtoscan/logo/sidebar-collapsed.svg`,
          onLight: `${pathToBrandingFolder}/avtoscan/logo/sidebar-collapsed.svg`,
        },
      },
    },
    customBackgroundImages: {
      onDark: `${pathToBrandingFolder}/avtoscan/login/light.jpg`,
      onLight: `${pathToBrandingFolder}/avtoscan/login/light.jpg`,
    },
    dataThemeAttr: 'avtoscan',
    faviconType: 'svg',
  },
  // 'palantirnavi.ru': {
  //   title: '',
  //   iconsPath: 'common/assets/logo/palantirnavi',
  // },
};
